/**
 * DocumentPlacement — what happens when a document is put into a folder.
 *
 * One controller for every way of doing it (drop on the folder tree, drop
 * on a folder tile, the Move dialog, and the Desktop's drags and cut/paste),
 * so they cannot drift apart. It asks `planDocumentPlacement` whether the
 * gesture is allowed and then either moves the file or says, in the user's
 * terms, why it can't:
 *
 *   move     → the folder move. Nothing about who can open the file changes.
 *   copy     → an email attachment taken into Documents: after the user
 *              agrees (one question for however many files), the server
 *              copies it and the original stays with its email — deleting
 *              the email would otherwise take a moved file with it.
 *   blocked  → nothing happens; a notice explains. A file in My-Fias belongs
 *              to the arche that keeps it there; Arche Saves and My-Fias
 *              accept nothing dropped into them.
 *
 * `place` resolves with what actually happened (`moved`, `copied`,
 * `cancelled`, `blocked`, `failed`, `none`) once the server has answered, so
 * a caller like the Desktop can keep an icon where it was unless the file
 * really moved. `placeMany` does a multi-select: refusals aren't attempted,
 * moves and copies run a few at a time, and ONE notice sums it up; each
 * result carries the new file for a copy. `preview` answers "would this drop
 * be allowed?" with the same planner, for hover feedback — it never moves
 * anything.
 *
 * A drop never grants an arche access. That is an explicit action on the file
 * (`GiveArcheAccessDialog`, below).
 *
 * The server re-decides every move and trusts none of this.
 */
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ShieldCheck } from 'lucide-react';
import api, { ApiError, type VaultDocumentDto, type VaultDocumentFolderDto } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCopyVaultDocument, useMoveVaultDocument } from '../hooks/useVaultDocuments';
import { planDocumentPlacement, type PlacementPlan } from '../lib/placement-plan';
import { runBounded } from '../lib/run-bounded';
import { NoticeBanner, type Notice } from './shared/NoticeBanner';
import { invalidateVaultDocumentsAfterHostWrite } from '@/arche/components/vaultHostWrites';
import { ARCHE_ID } from '../constants';

type PlacementDoc = Pick<VaultDocumentDto, 'documentId' | 'name' | 'folderId' | 'holderId'>;

/** What a placement actually did. `copied` left the original where it was
 *  and made a new file; `cancelled` is the user saying no to a copy. */
export type PlacementOutcome = 'none' | 'moved' | 'copied' | 'cancelled' | 'blocked' | 'failed';

/** One document's placement: what happened, and the new file for a copy. */
export interface PlacementResult {
  outcome: PlacementOutcome;
  copy?: VaultDocumentDto;
}

/** Moves in flight at once for a multi-select. */
const PLACE_CONCURRENCY = 4;

/** The display name of an arche, read off its My-Fias folder (the platform
 *  names that folder after the arche). Never the raw id. */
export function archeNameFor(
  archeId: string | null,
  folders: readonly VaultDocumentFolderDto[],
): string {
  if (!archeId) return 'an arche';
  return folders.find((f) => f.path === `/My-Fias/Arches/${archeId}`)?.name ?? 'an arche';
}

/** The words for a refused placement. */
export function describeBlockedPlacement(
  plan: Extract<PlacementPlan, { kind: 'blocked' }>,
  docName: string,
  folders: readonly VaultDocumentFolderDto[],
): string {
  if (plan.refusal === 'NOT_MOVABLE') {
    return plan.archeId
      ? `“${docName}” belongs to ${archeNameFor(plan.archeId, folders)} and can’t be moved.`
      : `“${docName}” is kept by Fias for an arche and can’t be moved.`;
  }
  if (plan.to === 'my-fias') {
    return 'Files can’t be moved into My-Fias. To let an arche open this file, choose “Give an arche access” on the file.';
  }
  if (plan.to === 'arche-saves') {
    return 'Files can’t be moved into Arche Saves. Move it somewhere in your Documents instead.';
  }
  return 'That file can’t go there.';
}

/** What a copy plan means, before the user agrees to it. */
export function describeCopyPlacement(docName: string): string {
  return `“${docName}” will be copied — email attachments stay with their email.`;
}

/** A copy's refusals, in the user's terms. */
function friendlyCopyError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'STORAGE_QUOTA_EXCEEDED') {
      return 'Your storage is full, so the copy wasn’t made.';
    }
    if (err.code === 'SOURCE_CHANGED') {
      // Its named label changed while it was being copied (ADR-039).
      return 'The file changed while it was being copied. Try again.';
    }
    if (
      err.code === 'AREA_READ_ONLY' ||
      err.code === 'VIEWER_READ_ONLY' ||
      err.code === 'VIEWER_CANNOT_WRITE'
    ) {
      return err.message;
    }
    if (err.status === 403) return "You don't have permission to copy this file.";
    if (err.status === 404) return 'That file or folder no longer exists.';
  }
  return err instanceof Error ? err.message : 'That didn’t work. Try again.';
}

/** A move's refusals, in the user's terms. Area codes carry their own
 *  message from the server; bare statuses are translated here. */
function friendlyMoveError(err: unknown): string {
  if (err instanceof ApiError) {
    if (
      err.code === 'NOT_MOVABLE' ||
      err.code === 'AREA_READ_ONLY' ||
      err.code === 'VIEWER_READ_ONLY'
    ) {
      return err.message;
    }
    if (err.code === 'ALREADY_SUPERSEDED') {
      return 'This is an older version of the file. Move the current version instead.';
    }
    if (err.code === 'CROSS_HOLDER_FOLDER' || err.status === 422) {
      return 'That folder belongs to a different workspace.';
    }
    if (err.status === 409) return 'This document is in the Trash. Restore it before moving.';
    if (err.status === 403) return "You don't have permission to move this document.";
    if (err.status === 404) return 'That file or folder no longer exists.';
  }
  return err instanceof Error ? err.message : 'That didn’t work. Try again.';
}

export interface DocumentPlacementController {
  /** Put `documentId` into `targetFolderId` (`null` = root), or explain why
   *  not. Resolves once the server has answered. */
  place: (
    documentId: string,
    targetFolderId: string | null,
    known?: PlacementDoc,
  ) => Promise<PlacementOutcome>;
  /** Place several documents; one notice sums it up. Resolves with each
   *  document's result (a copy's carries the new file). */
  placeMany: (
    documents: readonly PlacementDoc[],
    targetFolderId: string | null,
  ) => Promise<Map<string, PlacementResult>>;
  /** Would this placement be allowed? For hover feedback only. */
  preview: (
    document: Pick<VaultDocumentDto, 'name' | 'folderId'>,
    targetFolderId: string | null,
  ) => { plan: PlacementPlan; message: string | null };
  /** Say something through the placement banner. */
  notify: (notice: Notice | null) => void;
  /** The notice banner. Render once, anywhere in the page. */
  ui: React.ReactNode;
}

export function useDocumentPlacement(input: {
  folders: readonly VaultDocumentFolderDto[] | undefined;
}): DocumentPlacementController {
  const folders = useMemo(() => input.folders ?? [], [input.folders]);
  const move = useMoveVaultDocument();
  const copy = useCopyVaultDocument();
  const [notice, setNotice] = useState<Notice | null>(null);
  /** The copy question on screen, and how to answer it. */
  const [asking, setAsking] = useState<{
    names: string[];
    answer: (yes: boolean) => void;
  } | null>(null);

  /** Ask once whether to copy these files; resolves with the answer. */
  const askToCopy = useCallback(
    (docs: readonly PlacementDoc[]) =>
      new Promise<boolean>((resolve) => {
        setAsking({
          names: docs.map((d) => d.name),
          answer: (yes) => {
            setAsking(null);
            resolve(yes);
          },
        });
      }),
    [],
  );
  const copyInto = useCallback(
    (doc: PlacementDoc, targetFolderId: string | null) =>
      copy
        .mutateAsync({
          documentId: doc.documentId,
          holderId: doc.holderId,
          folderId: targetFolderId,
          onConflict: 'keep-both',
        })
        .then((res) => res.document),
    [copy],
  );

  const pathOf = useCallback(
    (folderId: string | null) =>
      folderId === null ? null : (folders.find((f) => f.folderId === folderId)?.path ?? null),
    [folders],
  );
  const planFor = useCallback(
    (doc: Pick<VaultDocumentDto, 'folderId'>, targetFolderId: string | null) =>
      planDocumentPlacement(
        { folderId: doc.folderId ?? null, folderPath: pathOf(doc.folderId ?? null) },
        { folderId: targetFolderId, path: pathOf(targetFolderId) },
      ),
    [pathOf],
  );

  const preview = useCallback<DocumentPlacementController['preview']>(
    (doc, targetFolderId) => {
      const plan = planFor(doc, targetFolderId);
      return {
        plan,
        message:
          plan.kind === 'blocked'
            ? describeBlockedPlacement(plan, doc.name, folders)
            : plan.kind === 'copy'
              ? describeCopyPlacement(doc.name)
              : null,
      };
    },
    [planFor, folders],
  );

  const place = useCallback<DocumentPlacementController['place']>(
    async (documentId, targetFolderId, known) => {
      setNotice(null);
      let doc: PlacementDoc;
      try {
        doc = known ?? (await api.getVaultDocument(documentId)).document;
      } catch (err) {
        setNotice({ tone: 'error', message: friendlyMoveError(err) });
        return 'failed';
      }
      const plan = planFor(doc, targetFolderId);
      switch (plan.kind) {
        case 'none':
          return 'none';
        case 'blocked':
          setNotice({ tone: 'info', message: describeBlockedPlacement(plan, doc.name, folders) });
          return 'blocked';
        case 'move':
          try {
            await move.mutateAsync({ documentId, targetFolderId });
            return 'moved';
          } catch (err) {
            setNotice({ tone: 'error', message: friendlyMoveError(err) });
            return 'failed';
          }
        case 'copy': {
          if (!(await askToCopy([doc]))) return 'cancelled';
          try {
            const made = await copyInto(doc, targetFolderId);
            setNotice({ tone: 'info', message: `Copied “${made.name}” to your Documents.` });
            return 'copied';
          } catch (err) {
            setNotice({ tone: 'error', message: friendlyCopyError(err) });
            return 'failed';
          }
        }
      }
    },
    [askToCopy, copyInto, folders, move, planFor],
  );

  const placeMany = useCallback<DocumentPlacementController['placeMany']>(
    async (documents, targetFolderId) => {
      setNotice(null);
      const results = new Map<string, PlacementResult>();
      const toMove: PlacementDoc[] = [];
      const toCopy: PlacementDoc[] = [];
      const refusals: string[] = [];
      for (const doc of documents) {
        const plan = planFor(doc, targetFolderId);
        if (plan.kind === 'none') results.set(doc.documentId, { outcome: 'none' });
        else if (plan.kind === 'blocked') {
          results.set(doc.documentId, { outcome: 'blocked' });
          refusals.push(describeBlockedPlacement(plan, doc.name, folders));
        } else if (plan.kind === 'copy') toCopy.push(doc);
        else toMove.push(doc);
      }
      // One question for every copy; a "no" leaves them where they are.
      const copying = toCopy.length > 0 && (await askToCopy(toCopy));
      if (!copying) {
        for (const doc of toCopy) results.set(doc.documentId, { outcome: 'cancelled' });
      }
      const work: Array<{ doc: PlacementDoc; kind: 'move' | 'copy' }> = [
        ...toMove.map((doc) => ({ doc, kind: 'move' as const })),
        ...(copying ? toCopy.map((doc) => ({ doc, kind: 'copy' as const })) : []),
      ];
      const settled = await runBounded(work, PLACE_CONCURRENCY, ({ doc, kind }) =>
        kind === 'move'
          ? move.mutateAsync({ documentId: doc.documentId, targetFolderId }).then(() => null)
          : copyInto(doc, targetFolderId),
      );
      const failures: string[] = [];
      let moved = 0;
      let copied = 0;
      settled.forEach((result, i) => {
        const { doc, kind } = work[i];
        if (result.status === 'fulfilled') {
          if (kind === 'move') {
            moved += 1;
            results.set(doc.documentId, { outcome: 'moved' });
          } else {
            copied += 1;
            results.set(doc.documentId, { outcome: 'copied', copy: result.value ?? undefined });
          }
        } else {
          results.set(doc.documentId, { outcome: 'failed' });
          failures.push(
            `“${doc.name}”: ${
              kind === 'move' ? friendlyMoveError(result.reason) : friendlyCopyError(result.reason)
            }`,
          );
        }
      });

      // Results in the order the documents came in.
      const ordered = new Map<string, PlacementResult>();
      for (const doc of documents) ordered.set(doc.documentId, results.get(doc.documentId)!);
      const attempted = documents.filter((d) => {
        const outcome = ordered.get(d.documentId)?.outcome;
        return outcome !== 'none' && outcome !== 'cancelled';
      }).length;
      if (attempted === 0) return ordered;
      if (documents.length === 1) {
        // One file reads like `place`.
        const only = refusals[0] ?? failures[0];
        if (only) setNotice({ tone: refusals.length ? 'info' : 'error', message: only });
        else if (copied === 1) {
          const made = [...ordered.values()][0]?.copy;
          setNotice({
            tone: 'info',
            message: `Copied “${made?.name ?? documents[0].name}” to your Documents.`,
          });
        }
        return ordered;
      }
      const parts: string[] = [];
      // "Moved m of n" counts what was meant to move (refusals included);
      // copies are counted on their own.
      const meantToMove = toMove.length + refusals.length;
      if (meantToMove > 0) parts.push(`Moved ${moved} of ${meantToMove}.`);
      if (copied > 0) parts.push(copied === 1 ? 'Copied 1 file.' : `Copied ${copied} files.`);
      if (refusals.length > 0) {
        parts.push(
          refusals.length === 1
            ? '1 can’t go there and stays where it is.'
            : `${refusals.length} can’t go there and stay where they are.`,
        );
      }
      if (failures.length > 0) {
        const verb =
          copying && toMove.length > 0 ? 'moved or copied' : copying ? 'copied' : 'moved';
        parts.push(`${failures.length} couldn’t be ${verb}.`);
      }
      setNotice({
        tone: failures.length > 0 ? 'error' : 'info',
        message: parts.join(' '),
        details: [...refusals, ...failures],
      });
      return ordered;
    },
    [askToCopy, copyInto, folders, move, planFor],
  );

  const ui = useMemo(
    () => (
      <>
        {notice && <NoticeBanner notice={notice} onDismiss={() => setNotice(null)} />}
        {asking && (
          <CopyInsteadDialog names={asking.names} onAnswer={(yes) => asking.answer(yes)} />
        )}
      </>
    ),
    [notice, asking],
  );

  // A stable object, so the views that hold it (the Desktop's shared
  // context) don't re-render for nothing.
  return useMemo(
    () => ({ place, placeMany, preview, notify: setNotice, ui }),
    [place, placeMany, preview, ui],
  );
}

/** "Copy this file instead?" — asked once for however many email
 *  attachments a placement would take into Documents. */
function CopyInsteadDialog({
  names,
  onAnswer,
}: {
  names: readonly string[];
  onAnswer: (yes: boolean) => void;
}) {
  const one = names.length === 1;
  return (
    <Dialog open onOpenChange={(next) => !next && onAnswer(false)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {one ? `Copy “${names[0]}” to Documents?` : `Copy ${names.length} files to Documents?`}
          </DialogTitle>
          <DialogDescription>
            Email attachments stay with their email — deleting the email would also remove a moved
            file, so Fias makes you {one ? 'a copy' : 'copies'}.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onAnswer(false)}>
            Cancel
          </Button>
          <Button autoFocus onClick={() => onAnswer(true)}>
            Copy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Give an arche access to a file — an explicit action, never a drop
// ---------------------------------------------------------------------------

/** The arches that keep a folder in this workspace's My-Fias — the ones the
 *  user already uses here — as `{ archeId, name }`, by name. */
export function archesWithFolders(
  folders: readonly VaultDocumentFolderDto[],
): Array<{ archeId: string; name: string }> {
  const out: Array<{ archeId: string; name: string }> = [];
  for (const folder of folders) {
    const match = /^\/My-Fias\/Arches\/([^/]+)$/.exec(folder.path);
    if (match) out.push({ archeId: match[1], name: folder.name });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

export function GiveArcheAccessDialog(props: {
  doc: Pick<VaultDocumentDto, 'documentId' | 'name'>;
  arches: ReadonlyArray<{ archeId: string; name: string }>;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const { doc, arches } = props;
  const [archeId, setArcheId] = useState<string>(arches[0]?.archeId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The arche cannot take a grant at all — explain, don't offer a retry. */
  const [cannot, setCannot] = useState<string | null>(null);
  const archeName = arches.find((a) => a.archeId === archeId)?.name ?? 'This arche';

  const grant = async () => {
    if (!archeId) return;
    setBusy(true);
    setError(null);
    try {
      await api.grantVaultArcheConsent({ granteeArcheId: archeId, documentIds: [doc.documentId] });
      // The file's access list and the Arche Access page now have one more
      // row; neither should wait out its stale time to show it.
      invalidateVaultDocumentsAfterHostWrite();
      props.onDone(
        `${archeName} can now open “${doc.name}”. You can change that under Arche Access.`,
      );
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError) {
        if (err.code === 'ARCHE_CANNOT_USE_GRANT' || err.code === 'ARCHE_NOT_FOUND') {
          setCannot(
            `${archeName} doesn’t open files from your Documents, so it can’t be given this one.`,
          );
          return;
        }
        if (err.code === 'SENSITIVITY_FORBIDDEN_FOR_ARCHE_GRANT') {
          setCannot('This file is marked proprietary, so arches can’t be given access to it.');
          return;
        }
      }
      setError(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
  };

  return (
    <Dialog open onOpenChange={(next) => !next && !busy && props.onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            Give an arche access
          </DialogTitle>
          <DialogDescription>
            Let an arche open “{doc.name}”. The file stays where it is.
          </DialogDescription>
        </DialogHeader>

        {arches.length === 0 ? (
          <p className="text-sm" role="status">
            No arches keep files in this space yet. Open an arche and use its own “open file” option
            to give it a file.
          </p>
        ) : cannot ? (
          <p className="text-sm" role="status">
            {cannot}
          </p>
        ) : (
          <div className="space-y-2 text-sm">
            <label className="block font-medium" htmlFor="give-arche-access-select">
              Arche
            </label>
            <select
              id="give-arche-access-select"
              className="w-full rounded-md border bg-background px-3 py-2"
              value={archeId}
              disabled={busy}
              onChange={(e) => setArcheId(e.target.value)}
            >
              {arches.map((a) => (
                <option key={a.archeId} value={a.archeId}>
                  {a.name}
                </option>
              ))}
            </select>
            <p className="text-muted-foreground">
              It will be able to read this one file. You can see and remove this at any time under{' '}
              <Link className="underline" to={`/a/${ARCHE_ID}/arche-access`}>
                Arche Access
              </Link>
              .
            </p>
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" disabled={busy} onClick={props.onClose}>
            {cannot || arches.length === 0 ? 'OK' : 'Not now'}
          </Button>
          {!cannot && arches.length > 0 && (
            <Button disabled={busy || !archeId} onClick={grant}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              Give access
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
